import React, { useEffect, useState } from "react";
import {
  RiEditLine,
  RiCameraLine,
  RiSaveLine,
  RiCloseLine,
} from "react-icons/ri";
import axios from "axios";
import toast from "react-hot-toast";

const ProfileSkeleton = () => {
  return (
    <div className="min-h-screen bg-gray-50 p-6 animate-pulse">
      {/* Header */}
      <div className="mb-4">
        <div className="h-8 w-48 bg-gray-300 rounded mb-2" />
        <div className="h-4 w-80 bg-gray-200 rounded" />
      </div>

      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Profile Header */}
        <div className="relative">
          <div className="h-32 bg-gray-200" />

          {/* Profile Picture */}
          <div className="absolute -bottom-16 left-8">
            <div className="w-32 h-32 rounded-2xl border-4 border-white overflow-hidden shadow-lg bg-gray-200" />
          </div>

          {/* Edit button */}
          <div className="absolute top-4 right-4 h-10 w-32 bg-gray-200 rounded-xl" />
        </div>

        {/* Profile Content */}
        <div className="pt-20 pb-8 px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Section title */}
            <div className="md:col-span-2 h-6 w-40 bg-gray-300 rounded mb-4" />

            {/* Name */}
            <div className="h-5 w-3/4 bg-gray-200 rounded" />
            {/* Email */}
            <div className="h-5 w-2/3 bg-gray-200 rounded" />
            {/* Phone */}
            <div className="h-5 w-1/2 bg-gray-200 rounded" />
            {/* Gender */}
            <div className="h-5 w-1/3 bg-gray-200 rounded" />
            {/* DOB */}
            <div className="h-5 w-2/5 bg-gray-200 rounded" />
            {/* Nationality */}
            <div className="h-5 w-1/4 bg-gray-200 rounded" />
            {/* Address */}
            <div className="md:col-span-2 h-16 w-full bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    </div>
  );
};

const Profile = () => {
  const [user, setUser] = useState(null);
  const [tempUser, setTempUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const adminId = JSON.parse(localStorage.getItem("strikes-shots-admin"))?.id;

  // Fetch admin data
  const fetchUserData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admins/admin-by-id/${adminId}`
      );
      setUser(response.data.admin);
      setTempUser(response.data.admin);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleEdit = () => setIsEditing(true);

  const handleCancel = () => {
    setTempUser(user);
    setIsEditing(false);
  };

  // Save all changes, including profilePic
  const handleSave = async () => {
    setLoading(true);
    try {
      setIsSubmitting(true);
      // Only send allowed fields
      const payload = {
        name: tempUser.name,
        email: tempUser.email,
        phoneNumber: tempUser.phoneNumber,
        dateOfBirth: tempUser.dateOfBirth,
        gender: tempUser.gender,
        nationality: tempUser.nationality,
        address: tempUser.address,
        profilePic: tempUser.profilePic,
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/admins/edit-admin/${adminId}`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );
      setUser(response.data.admin);
      setIsEditing(false);
      toast.success("Profile Updated Successfully");
    } catch (error) {
      console.log("Save error:", error.response?.data || error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTempUser({ ...tempUser, [name]: value });
  };

  // Upload image to Cloudinary
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "dev-wb"); // Your preset
    formData.append("cloud_name", "dqxlte6wf"); // Your cloud name

    try {
      const res = await fetch(
        `https://api.cloudinary.com/v1_1/dqxlte6wf/image/upload`,
        {
          method: "POST",
          body: formData,
        }
      );
      const data = await res.json();
      setTempUser({ ...tempUser, profilePic: data.secure_url });
    } catch (error) {
      console.log("Cloudinary upload error:", error);
    } finally {
      setImageUploading(false);
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const calculateAge = (dateString) => {
    const today = new Date();
    const birthDate = new Date(dateString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    )
      age--;
    return age;
  };

  if (!user) return <ProfileSkeleton />;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="">
        {/* Header */}
        <div className="mb-4">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent">
              User Profile
            </h1>
          </div>
          <p className="text-gray-600 mt-2">
            Manage your account information and preferences
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Profile Header */}
          <div className="relative">
            <div className="h-32 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor"></div>

            {/* Profile Picture */}
            <div className="absolute -bottom-16 left-8">
              <div className="relative">
                <div className="w-32 h-32 rounded-2xl border-4 border-white overflow-hidden shadow-lg">
                  <img
                    src={
                      tempUser.profilePic || "https://via.placeholder.com/150"
                    }
                    alt={tempUser.name}
                    className="w-full h-full object-cover"
                  />
                  {imageUploading && (
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center text-white font-bold">
                      Uploading...
                    </div>
                  )}
                </div>

                {isEditing && (
                  <label className="absolute bottom-0 right-0 bg-primaryBtnColor text-white p-2 rounded-full shadow-md cursor-pointer hover:bg-primaryBtnHoverColor transition-colors">
                    <RiCameraLine size={18} />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Edit/Save Buttons */}
            <div className="absolute top-4 right-4">
              {!isEditing ? (
                <button
                  onClick={handleEdit}
                  className="bg-primaryBtnColor text-white py-2 px-4 rounded-xl flex items-center shadow-lg hover:bg-primaryBtnHoverColor transition-colors"
                >
                  <RiEditLine className="mr-2" /> Edit Profile
                </button>
              ) : (
                <div className="flex space-x-3">
                  <button
                    onClick={handleCancel}
                    className="bg-gray-300 text-gray-800 py-2 px-4 rounded-xl flex items-center shadow hover:bg-gray-400 transition-colors"
                  >
                    <RiCloseLine className="mr-2" /> Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={loading || imageUploading}
                    className="bg-primaryBtnColor text-white py-2 px-4 rounded-xl w-44 flex items-center justify-center shadow hover:bg-primaryBtnHoverColor transition-colors"
                  >
                    {loading ? (
                      <div class="spinner w-full"></div>
                    ) : (
                      <>
                        <RiSaveLine className="mr-2" /> Save Changes
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Profile Content */}
          <div className="pt-20 pb-8 px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Personal Information */}
              <div className="md:col-span-2">
                <h2 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                  Personal Information
                </h2>
              </div>

              {/* Name */}
              <div>
                <label className="block text-gray-500 mb-2">Full Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="name"
                    value={tempUser.name}
                    onChange={handleChange}
                    className="w-full bg-gray-100 border border-gray-300 rounded-lg p-3"
                  />
                ) : (
                  <p className="text-gray-900 text-lg">{user.name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-gray-500 mb-2">
                  Email Address
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    name="email"
                    value={tempUser.email}
                    onChange={handleChange}
                    className="w-full bg-gray-100 border border-gray-300 rounded-lg p-3"
                  />
                ) : (
                  <p className="text-gray-900 text-lg">{user.email}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-gray-500 mb-2">Phone Number</label>
                {isEditing ? (
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={tempUser.phoneNumber}
                    onChange={handleChange}
                    className="w-full bg-gray-100 border border-gray-300 rounded-lg p-3"
                  />
                ) : (
                  <p className="text-gray-900 text-lg">{user.phoneNumber}</p>
                )}
              </div>

              {/* Gender */}
              <div>
                <label className="block text-gray-500 mb-2">Gender</label>
                {isEditing ? (
                  <select
                    name="gender"
                    value={tempUser.gender}
                    onChange={handleChange}
                    className="w-full bg-gray-100 border border-gray-300 rounded-lg p-3"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                ) : (
                  <p className="text-gray-900 capitalize">{user.gender}</p>
                )}
              </div>

              {/* Date of Birth */}
              <div>
                <label className="block text-gray-500 mb-2">
                  Date of Birth
                </label>
                {isEditing ? (
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={tempUser.dateOfBirth.split("T")[0]}
                    onChange={handleChange}
                    className="w-full bg-gray-100 border border-gray-300 rounded-lg p-3"
                  />
                ) : (
                  <p className="text-gray-900 text-lg">
                    {formatDate(user.dateOfBirth)} (
                    {calculateAge(user.dateOfBirth)} yrs)
                  </p>
                )}
              </div>

              {/* Nationality */}
              <div>
                <label className="block text-gray-500 mb-2">Nationality</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="nationality"
                    value={tempUser.nationality}
                    onChange={handleChange}
                    className="w-full bg-gray-100 border border-gray-300 rounded-lg p-3"
                  />
                ) : (
                  <p className="text-gray-900">{user.nationality}</p>
                )}
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <label className="block text-gray-500 mb-2">Address</label>
                {isEditing ? (
                  <textarea
                    name="address"
                    value={tempUser.address}
                    onChange={handleChange}
                    rows="3"
                    className="w-full bg-gray-100 border border-gray-300 rounded-lg p-3"
                  />
                ) : (
                  <p className="text-gray-900">{user.address}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
